'use client';

import { get, kebabCase } from 'lodash-es';
import Typography from '@ui/Typography';
import useShareModalStore from '@/components/store/shareModalSlice';
import Button from '@ui/Button';
import Input from '@ui/Input';
import React, { useState, useEffect } from 'react';
import { MdContentCopy, MdDone } from 'react-icons/md';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import { usePathname, useRouter } from 'next/navigation';
import {
  getEnvBaseUrl,
  getLoginUrlByOrgId,
  joinUrl,
} from '@/utils/stringHelpers';
import { DOMAIN_PREFIX_REDIRECTS } from '@/constants/orgConfig.constant';

const iconSize = 65;
const smallIconSize = 40;
const windowSize = {
  width: 1024,
  height: 768,
};

function ActivitySuccessDonateLoggedOut(props) {
  const {
    user = {},
    post = null,
    reward = null,
    donate = false,
    isPost = false,
    nonProfit = null,
    originPage = null,
    isPostPublic = false,
    onContinue = () => {},
    hasUserSession = false,
    donateType,
    donationMessageTemplates,
  } = props;

  const badge = get(reward, 'badges.badge', null);
  const pointsEarned = get(reward, 'points.earned', 0);

  const router = useRouter();
  const nekiBaseUrl = getEnvBaseUrl();

  const pathName = usePathname();

  const metadata = useShareModalStore((state) => state.metadata);

  const [shareUrl, setShareUrl] = useState('');

  const createDonationShareLink = () => {
    let url = `nonprofit/${kebabCase(nonProfit.name)}-${nonProfit.id}`;
    if (nonProfit?.company?.publicProfile?.enabled) {
      url = nonProfit?.company?.publicProfile?.slug;
    }
    url = url || `${window?.location?.origin}${pathName}`;

    if (originPage) {
      url = `${window?.location?.origin}${originPage}`;
    }

    if (!url.startsWith(window?.location?.origin)) {
      url = joinUrl(window?.location?.origin || nekiBaseUrl, url);
    }
    return url;
  };

  const title = metadata.title || 'Neki';
  const randomMessage = donationMessageTemplates[
    Math.floor(Math.random() * donationMessageTemplates.length)
  ].replace(/\[Nonprofit Name\]/g, `${nonProfit?.name}`);
  // const description = `Just donated to ${nonProfit?.name} on Neki — let's inspire change together!`;
  const description = randomMessage;

  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 600);
    }
  }, [copied]);

  useEffect(() => {
    const url = createDonationShareLink();
    setShareUrl(url);
  }, []);

  const handleLinkCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
  };

  const isLessThanLargeScreen = useBreakpointHooks((bp) => bp.useSmaller('lg'));
  const isLessThanMediumScreen = useBreakpointHooks((bp) =>
    bp.useSmaller('md'),
  );

  const handleRedirect = () => {
    let url = `nonprofit/${kebabCase(nonProfit.name)}-${nonProfit.id}`;
    if (nonProfit?.company?.publicProfile?.enabled) {
      url = nonProfit?.company?.publicProfile?.slug;
    }

    if (originPage) {
      url = originPage;
      router.push(originPage);
    } else {
      onContinue();
      // router.push(`/${url || 'nonprofit'}`);
    }
  };

  function handleSignUpRedirect() {
    const loginUrlByHostName =
      DOMAIN_PREFIX_REDIRECTS[window.location.hostname];
    const loginUrlByOrgId = getLoginUrlByOrgId(nonProfit?.company_id);
    const loginUrl = loginUrlByHostName || loginUrlByOrgId;
    if (loginUrl) {
      return loginUrl;
    }
    const signUpPath = `/onboard/nonprofit-user?nonProfitId=${nonProfit.id}`;
    const signUpRedirectLink = `${nekiBaseUrl}/api/auth/signup?returnTo=${encodeURIComponent(
      signUpPath,
    )}`;
    return signUpRedirectLink;
  }

  function handleLogInRedirect() {
    const loginUrlByHostName =
      DOMAIN_PREFIX_REDIRECTS[window.location.hostname];
    const loginUrlByOrgId = getLoginUrlByOrgId(nonProfit?.company_id);
    const loginUrl = loginUrlByHostName || loginUrlByOrgId;
    if (loginUrl) {
      return loginUrl;
    }
    const loginLink = `${nekiBaseUrl}/api/auth/logout`;
    return loginLink;
  }

  return (
    <div className="my-4 flex flex-col items-center">
      <div className={`flex w-full flex-col justify-center divide-y`}>
        <div className="flex-1/2 flex flex-col items-center justify-center gap-3">
          <img
            src={'/assets/images/neki-heart.png'}
            alt="donation-heart"
            className="h-24 w-24"
          />
          <Typography
            component="div"
            className="text-center text-32"
            variant="h1">
            {user?.first_name || user?.firstName || 'Donor'}, Thank you for
            making an impact!
          </Typography>
          <Typography
            component="div"
            color="textDisabled"
            className="mx-[70px] text-center font-medium"
            variant="h4">
            You will receive the receipt directly from the nonprofit.
          </Typography>
        </div>
        <div
          className={`flex-1/2 mt-6 flex flex-col items-center justify-center gap-10`}>
          {!nonProfit?.company_id ? (
            <div className="mt-4 flex w-full flex-col items-center justify-center gap-4">
              <Typography variant="h2" component="h2" className="text-center">
                Sign Up on Neki to Join {nonProfit.name || 'Nonprofit'}
              </Typography>
              <Button
                as="a"
                variant="filled"
                color="primary"
                href={handleSignUpRedirect()}
                target="_blank"
                className="px-5">
                Sign Up
              </Button>
              <Typography variant="body1">
                Already have an account on Neki?{' '}
                <Typography
                  component="a"
                  href={handleLogInRedirect()}
                  variant="body1"
                  className="text-text-hyperlink hover:underline">
                  Log In
                </Typography>
              </Typography>
            </div>
          ) : (
            <>
              <Typography
                component="div"
                className={`mt-6 text-center font-medium`}
                variant="h4">
                Share
                {donateType === 'user-external-donation'
                  ? ' this giving opportunity'
                  : ' your donation'}{' '}
                on social media and inspire others to participate in your cause!
              </Typography>
              <div
                className={`flex flex-wrap items-center justify-start gap-4`}>
                <EmailShareButton
                  url={shareUrl}
                  subject={title}
                  body={`${description} \n\n`}>
                  <EmailIcon
                    size={isLessThanMediumScreen ? smallIconSize : iconSize}
                    windowWidth={windowSize.width}
                    windowHeight={windowSize.height}
                    round
                  />
                </EmailShareButton>

                <WhatsappShareButton
                  url={shareUrl}
                  title={title}
                  separator=" "
                  windowWidth={windowSize.width}
                  windowHeight={windowSize.height}
                  //   onClick={handleClose}
                >
                  <WhatsappIcon
                    size={isLessThanMediumScreen ? smallIconSize : iconSize}
                    round
                  />
                </WhatsappShareButton>

                <FacebookShareButton
                  url={shareUrl}
                  quote={title}
                  windowWidth={windowSize.width}
                  windowHeight={windowSize.height}
                  //   onClick={handleClose}
                >
                  <FacebookIcon
                    size={isLessThanMediumScreen ? smallIconSize : iconSize}
                    round
                  />
                </FacebookShareButton>

                <TwitterShareButton
                  url={shareUrl}
                  title={title}
                  windowWidth={windowSize.width}
                  windowHeight={windowSize.height}
                  //   onClick={handleClose}
                >
                  <TwitterIcon
                    size={isLessThanMediumScreen ? smallIconSize : iconSize}
                    round
                  />
                </TwitterShareButton>

                <LinkedinShareButton
                  url={shareUrl}
                  title={encodeURIComponent(title)}
                  summary={encodeURIComponent(description)}
                  source="Neki"
                  windowWidth={windowSize.width}
                  windowHeight={windowSize.height}
                  //   onClick={handleClose}
                >
                  <LinkedinIcon
                    size={isLessThanMediumScreen ? smallIconSize : iconSize}
                    round
                  />
                </LinkedinShareButton>
              </div>
              <div className="flex w-full max-w-[700px] items-center justify-between gap-6">
                <div className="flex-1">
                  <Input value={shareUrl} disabled />
                </div>
                <Button
                  variant="filled"
                  color="primary"
                  onClick={handleLinkCopy}>
                  {!copied ? (
                    <MdContentCopy />
                  ) : (
                    <MdDone className="animate-pulse" />
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      <Typography
        component="div"
        variant="hyperlink"
        className="my-4 cursor-pointer text-center text-15"
        onClick={handleRedirect}>
        <u>or Continue...</u>
      </Typography>
    </div>
  );
}

export default ActivitySuccessDonateLoggedOut;
